import React from "react";
import { useQuery } from "@tanstack/react-query";
import { Building2, LocateFixed, Mountain, Search, TrainFront, X } from "lucide-react";
import { searchWts, type WhereToStartSearchResult } from "../api/where-to-start";

// Place search for Where to start (spec 31): a combobox over the gazetteer
// (towns, postcodes, stations, car parks, hills). Picking a result is handled
// by the page; this component owns the text, the dropdown and the keyboard.

const GROUPS: Array<{ kind: WhereToStartSearchResult["kind"]; label: string }> = [
  { kind: "place", label: "Towns and villages" },
  { kind: "postcode", label: "Postcodes" },
  { kind: "station", label: "Stations" },
  { kind: "car_park", label: "Car parks" },
  { kind: "peak", label: "Hills" },
];

function Icon({ kind }: { kind: WhereToStartSearchResult["kind"] }) {
  if (kind === "station") return <TrainFront size={14} aria-hidden="true" />;
  if (kind === "car_park") return <span aria-hidden="true">P</span>;
  if (kind === "peak") return <Mountain size={14} aria-hidden="true" />;
  return <Building2 size={14} aria-hidden="true" />;
}

function Highlight({ text, q, bold }: { text: string; q: string; bold?: boolean }) {
  const i = text.toLowerCase().indexOf(q.toLowerCase());
  if (i < 0 || q.length === 0) return <>{text}</>;
  return (
    <>
      {text.slice(0, i)}
      <b style={{ color: bold ? "var(--text-secondary)" : "var(--text-accent)", fontWeight: 700 }}>
        {text.slice(i, i + q.length)}
      </b>
      {text.slice(i + q.length)}
    </>
  );
}

export interface WhereToStartSearchProps {
  /** Chosen place (shown as a chip in the field); null = Anywhere. */
  place: string | null;
  onPick: (r: WhereToStartSearchResult) => void;
  onClear: () => void;
  onNearMe: () => void;
  nearMeBusy?: boolean;
}

export function WhereToStartSearch({ place, onPick, onClear, onNearMe, nearMeBusy }: WhereToStartSearchProps) {
  const [text, setText] = React.useState("");
  const [debounced, setDebounced] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [active, setActive] = React.useState(0);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const listId = React.useId();

  React.useEffect(() => {
    const t = window.setTimeout(() => setDebounced(text.trim()), 200);
    return () => window.clearTimeout(t);
  }, [text]);

  const q = debounced.length >= 2 ? debounced : "";
  const query = useQuery({
    queryKey: ["where-to-start/search", q],
    queryFn: () => searchWts(q),
    enabled: q.length >= 2,
    staleTime: 60_000,
    retry: false,
  });

  const groups = React.useMemo(() => {
    const results = query.data?.results ?? [];
    return GROUPS.map((g) => ({ ...g, items: results.filter((r) => r.kind === g.kind) })).filter((g) => g.items.length > 0);
  }, [query.data]);
  const flat = React.useMemo(() => groups.flatMap((g) => g.items), [groups]);

  React.useEffect(() => setActive(0), [flat]);

  // Close when focus or a click lands outside the combobox.
  React.useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (r: WhereToStartSearchResult) => {
    setText("");
    setDebounced("");
    setOpen(false);
    onPick(r);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      if (open) {
        e.preventDefault();
        setOpen(false);
      }
      return;
    }
    if (!open && (e.key === "ArrowDown" || e.key === "ArrowUp") && flat.length > 0) {
      setOpen(true);
      return;
    }
    if (flat.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => (a + 1) % flat.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => (a - 1 + flat.length) % flat.length);
    } else if (e.key === "Enter" && open) {
      e.preventDefault();
      const r = flat[active];
      if (r) pick(r);
    }
  };

  const showList = open && q.length >= 2 && !query.isError;
  const optId = (i: number) => `${listId}-opt-${i}`;
  let idx = -1;

  return (
    <div ref={rootRef} style={{ position: "relative" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          minHeight: 44,
          padding: "0 6px 0 12px",
          background: "var(--surface-sunken)",
          border: "1px solid var(--border-default)",
          borderRadius: 10,
          boxSizing: "border-box",
        }}
        className="wts-field"
      >
        <Search size={18} aria-hidden="true" style={{ color: "var(--text-muted)", flexShrink: 0 }} />
        {place ? (
          <span
            style={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <span
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                maxWidth: "100%",
                height: 28,
                padding: "0 4px 0 10px",
                borderRadius: 999,
                background: "var(--accent-soft)",
                border: "1px solid var(--border-gold)",
                color: "var(--text-accent)",
                font: "var(--type-label)",
                fontWeight: 600,
              }}
            >
              <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{place}</span>
              <button
                type="button"
                onClick={() => {
                  onClear();
                  window.setTimeout(() => inputRef.current?.focus(), 0);
                }}
                aria-label={`Clear ${place} and go back to Anywhere`}
                style={{
                  width: 22,
                  height: 22,
                  display: "grid",
                  placeItems: "center",
                  border: 0,
                  borderRadius: "50%",
                  background: "transparent",
                  color: "inherit",
                  cursor: "pointer",
                  padding: 0,
                }}
              >
                <X size={14} aria-hidden="true" />
              </button>
            </span>
          </span>
        ) : (
          <input
            ref={inputRef}
            role="combobox"
            aria-expanded={showList}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={showList && flat.length > 0 ? optId(active) : undefined}
            aria-label="Search for a place"
            placeholder="Anywhere, or a town, station or postcode"
            value={text}
            onChange={(e) => {
              setText(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={onKeyDown}
            autoComplete="off"
            spellCheck={false}
            style={{
              flex: 1,
              minWidth: 0,
              background: "none",
              border: 0,
              outline: 0,
              boxShadow: "none",
              color: "var(--text-primary)",
              font: "inherit",
              fontSize: 15,
              fontWeight: 500,
            }}
          />
        )}
        <button
          type="button"
          onClick={onNearMe}
          disabled={nearMeBusy}
          aria-label="Use my location"
          style={{
            height: 32,
            padding: "0 10px",
            borderRadius: 8,
            border: "1px solid var(--border-strong)",
            background: "var(--surface-raised)",
            color: "var(--text-primary)",
            font: "var(--type-label)",
            fontSize: 12,
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: 5,
            cursor: nearMeBusy ? "wait" : "pointer",
            flexShrink: 0,
          }}
        >
          <LocateFixed size={13} aria-hidden="true" />
          Near me
        </button>
      </div>

      {showList && (
        <div
          id={listId}
          role="listbox"
          aria-label="Places"
          style={{
            position: "absolute",
            left: 0,
            right: 0,
            top: "calc(100% + 6px)",
            zIndex: 20,
            background: "var(--surface-card)",
            border: "1px solid var(--border-strong)",
            borderRadius: 12,
            boxShadow: "var(--shadow-lg)",
            padding: 6,
            display: "flex",
            flexDirection: "column",
            maxHeight: 420,
            overflowY: "auto",
          }}
        >
          {query.isLoading && (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>Searching</div>
          )}
          {!query.isLoading && flat.length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 13, color: "var(--text-muted)" }}>
              No places found. Try a town, a station or a postcode.
            </div>
          )}
          {groups.map((g) => (
            <div key={g.kind} role="group" aria-label={g.label}>
              <div className="eyebrow" style={{ fontSize: 10, padding: "10px 12px 4px" }}>
                {g.label}
              </div>
              {g.items.map((r) => {
                idx += 1;
                const i = idx;
                const isActive = i === active;
                return (
                  <div
                    key={`${r.kind}-${r.id ?? r.name}-${r.lat}-${r.lng}`}
                    id={optId(i)}
                    role="option"
                    aria-selected={isActive}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      pick(r);
                    }}
                    onMouseEnter={() => setActive(i)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 12,
                      padding: "9px 12px",
                      minHeight: 44,
                      boxSizing: "border-box",
                      borderRadius: 8,
                      cursor: "pointer",
                      background: isActive ? "var(--surface-raised)" : "transparent",
                      color: "var(--text-primary)",
                    }}
                  >
                    <span
                      style={{
                        width: 28,
                        height: 28,
                        borderRadius: 7,
                        flex: "none",
                        display: "grid",
                        placeItems: "center",
                        background: "var(--surface-sunken)",
                        border: "1px solid var(--border-default)",
                        color: "var(--text-secondary)",
                        font: "700 13px var(--font-display)",
                      }}
                    >
                      <Icon kind={r.kind} />
                    </span>
                    <span style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ fontWeight: 600, fontSize: 14 }}>
                        <Highlight text={r.name} q={debounced} />
                      </span>
                      {r.detail && (
                        <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
                          <Highlight text={r.detail} q={debounced} bold />
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          ))}
          <div
            style={{
              marginTop: 6,
              padding: "10px 12px",
              borderTop: "1px solid var(--border-subtle)",
              fontSize: 12,
              color: "var(--text-muted)",
            }}
          >
            Postcodes work too, like SK22 2.
          </div>
        </div>
      )}
    </div>
  );
}
